import { useCallback, useState } from 'react'
import type { WorkOrderSectionFilter } from './WorkOrderNavBarNext'

/**
 * The Work Order's All / Areas / Surfaces filter, remembered in `localStorage` — per browser, not per
 * quote or per user, and never written onto the quote. Without it the filter reset to All every time
 * the Work Order tab mounted (ENG-2003).
 */
export const WORK_ORDER_SECTION_FILTER_KEY = 'quoteOptions.workOrderSectionFilter'

const FILTERS: WorkOrderSectionFilter[] = ['all', 'area', 'substrate']

/** Defaults to `'all'` — on a missing key, an unknown value, or no `window`. */
export function readWorkOrderSectionFilter(): WorkOrderSectionFilter {
  if (typeof window === 'undefined') return 'all'
  try {
    const stored = window.localStorage.getItem(WORK_ORDER_SECTION_FILTER_KEY)
    return FILTERS.includes(stored as WorkOrderSectionFilter) ? (stored as WorkOrderSectionFilter) : 'all'
  } catch {
    return 'all'
  }
}

export function writeWorkOrderSectionFilter(filter: WorkOrderSectionFilter): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(WORK_ORDER_SECTION_FILTER_KEY, filter)
  } catch {
    // A full/blocked localStorage shouldn't break the filter — the choice just won't persist.
  }
}

/** `useState` for the Work Order section filter, persisted across tabs, quotes and reloads. */
export function useWorkOrderSectionFilter() {
  const [filter, setFilterState] = useState<WorkOrderSectionFilter>(readWorkOrderSectionFilter)

  const setFilter = useCallback((next: WorkOrderSectionFilter) => {
    writeWorkOrderSectionFilter(next)
    setFilterState(next)
  }, [])

  return [filter, setFilter] as const
}
